import React, { useState, useEffect, useRef } from "react";
import {
	X,
	Clock,
	Users,
	Video,
	MapPin,
	ChevronDown,
	Bell,
	FileText,
	Calendar,
	Bold,
	Italic,
	Underline,
	List,
	ListOrdered,
	Coffee,
	Globe,
	Lock,
} from "lucide-react";
import { format, startOfDay, parse } from "date-fns";

// Import the modal components (same as CreateEvent)
import TimezoneDropdown from "./TimezoneDropdown";
import RepeatDropdown from "./RepeatDropdown";
import ParticipantsModal from "./ParticipantsModal";
import ConferencingModal from "./ConferencingModal";
import LocationModal from "./LocationModal";
import DocumentsModal from "./DocumentsModal";
import RemindersModal from "./RemindersModal";
import StatusModal from "./StatusModal";
import VisibilityModal from "./VisibilityModal";

const TimePickerDropdown = ({ value, onChange, isStart = true }) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);
	const selectedItemRef = useRef(null);

	// Generate time options in 15-minute intervals
	const generateTimeOptions = () => {
		const options = [];
		for (let hour = 0; hour < 24; hour++) {
			for (let minute = 0; minute < 60; minute += 15) {
				const formattedHour = hour % 12 || 12;
				const period = hour < 12 ? "AM" : "PM";
				const formattedMinute = minute.toString().padStart(2, "0");
				const timeString = `${formattedHour}:${formattedMinute} ${period}`;
				const value = `${hour.toString().padStart(2, "0")}:${formattedMinute}`;
				options.push({ label: timeString, value });
			}
		}
		return options;
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		if (isOpen && selectedItemRef.current) {
			setTimeout(() => {
				selectedItemRef.current.scrollIntoView({
					block: "center",
					behavior: "auto",
				});
			}, 0);
		}
	}, [isOpen]);

	const timeOptions = generateTimeOptions();
	const selectedTime =
		timeOptions.find((opt) => opt.value === value)?.label || value;

	return (
		<div className="relative" ref={dropdownRef}>
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className="bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none"
			>
				{selectedTime}
			</button>

			{isOpen && (
				<div className="absolute z-50 mt-1 max-h-60 w-32 overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 scrollbar scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600">
					{timeOptions.map((option) => (
						<div
							key={option.value}
							ref={option.value === value ? selectedItemRef : null}
							className={`cursor-pointer px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 
                ${
									value === option.value
										? "bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400"
										: "text-gray-900 dark:text-gray-100"
								}`}
							onClick={() => {
								onChange(option.value);
								setIsOpen(false);
							}}
						>
							{option.label}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

const convert24To12 = (time24) => {
	try {
		const [hours24, minutes] = time24.split(":");
		const hours = parseInt(hours24);
		const period = hours >= 12 ? "PM" : "AM";
		const hours12 = hours % 12 || 12;
		return `${hours12}:${minutes} ${period}`;
	} catch (error) {
		console.error("Time conversion error:", error);
		return time24;
	}
};

// Update getInitialFormData to handle the timezone properly
const getInitialFormData = (event) => {

	// Format the date string directly from the provided Date objects
	const startDate = new Date(event.start);
	const endDate = new Date(event.end);

	// Get time components
	const startHours = startDate.getHours().toString().padStart(2, "0");
	const startMinutes = startDate.getMinutes().toString().padStart(2, "0");
	const endHours = endDate.getHours().toString().padStart(2, "0");
	const endMinutes = endDate.getMinutes().toString().padStart(2, "0");

	// Format times
	const startTime = `${startHours}:${startMinutes}`;
	const endTime = `${endHours}:${endMinutes}`;

	// Get UTC values to avoid timezone shifts
	const year = startDate.getUTCFullYear();
	const month = (startDate.getUTCMonth() + 1).toString().padStart(2, "0");
	const day = startDate.getUTCDate().toString().padStart(2, "0");

	// Format date using UTC values
	const date = `${year}-${month}-${day}`;

	return {
		title: event.title || "",
		startTime: startTime,
		endTime: endTime,
		displayStartTime: convert24To12(startTime),
		displayEndTime: convert24To12(endTime),
		date: date,
		isAllDay: event.allDay || false,
		timezone:
			event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
		repeat: event.repeat || "none",
		participants: (event.attendees || []).map((a) => a.email),
		conferenceData: event.conferenceData || null,
		location: event.location || "",
		description: event.description || "",
		documents: [],
		reminders: event.reminders?.useDefault
			? ["30"]
			: event.reminders?.overrides?.map((r) => r.minutes.toString()) || ["30"],
		visibility: event.visibility || "default",
		status: event.status || "confirmed",
	};
};

const EditEvent = ({ event, onSave, onClose, isOpen }) => {
	const [formData, setFormData] = useState(() => getInitialFormData(event));

	const [isTimezoneOpen, setIsTimezoneOpen] = useState(false);
	const [isRepeatOpen, setIsRepeatOpen] = useState(false);
	const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
	const [isConferencingOpen, setIsConferencingOpen] = useState(false);
	const [isLocationOpen, setIsLocationOpen] = useState(false);
	const [isDocumentsOpen, setIsDocumentsOpen] = useState(false);
	const [isRemindersOpen, setIsRemindersOpen] = useState(false);
	const [isStatusOpen, setIsStatusOpen] = useState(false);
	const [isVisibilityOpen, setIsVisibilityOpen] = useState(false);
	const [errors, setErrors] = useState([]);
	const [isInteractionEnabled, setIsInteractionEnabled] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isRichTextFocused, setIsRichTextFocused] = useState(false);
	const [activeFormats, setActiveFormats] = useState({
		bold: false,
		italic: false,
		underline: false,
		insertUnorderedList: false,
		insertOrderedList: false,
	});

	const editorRef = useRef(null);
	const dateInputRef = useRef(null);

	useEffect(() => {
		if (editorRef.current) {
			editorRef.current.innerHTML = formData.description;
		}
	}, []);

	useEffect(() => {
		if (isOpen) {
			const timer = setTimeout(() => {
				setIsInteractionEnabled(true);
			}, 300);

			return () => {
				clearTimeout(timer);
				setIsInteractionEnabled(false);
			};
		}
	}, [isOpen]);
	const handleFormat = (command) => {
		if (!editorRef.current?.contains(document.activeElement)) {
			editorRef.current?.focus();
		}

		if (command === "insertUnorderedList" || command === "insertOrderedList") {
			const selection = window.getSelection();
			const editor = editorRef.current;

			if (editor.innerHTML === "" || editor.innerHTML === "<br>") {
				editor.innerHTML =
					command === "insertUnorderedList"
						? "<ul><li><br></li></ul>"
						: "<ol><li><br></li></ol>";

				const li = editor.querySelector("li");
				const newRange = document.createRange();
				newRange.setStart(li, 0);
				newRange.collapse(true);
				selection.removeAllRanges();
				selection.addRange(newRange);
			} else {
				document.execCommand(command, false);
			}
		} else {
			document.execCommand(command, false);
		}

		setTimeout(() => {
			setActiveFormats({
				bold: document.queryCommandState("bold"),
				italic: document.queryCommandState("italic"),
				underline: document.queryCommandState("underline"),
				insertUnorderedList: document.queryCommandState("insertUnorderedList"),
				insertOrderedList: document.queryCommandState("insertOrderedList"),
			});
		}, 0);
	};

	const getFormatButtonClass = (isActive) => `
    p-1 rounded transition-colors
    ${
			isActive
				? "bg-gray-200 dark:bg-gray-700 text-blue-600 dark:text-blue-400"
				: "hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
		}
  `;

	const validateFormData = (data) => {
		const errors = [];

		if (!data.title?.trim()) {
			errors.push("Title is required");
		}

		try {
			// Validate the date and times
			const startDateTime = new Date(`${data.date}T${data.startTime}`);
			const endDateTime = new Date(`${data.date}T${data.endTime}`);

			if (!data.isAllDay) {
				if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
					errors.push("Invalid date or time format");
				} else if (endDateTime <= startDateTime) {
					errors.push("End time must be after start time");
				}
			}
		} catch (error) {
			console.error("Date validation error:", error);
			errors.push("Invalid date or time format");
		}

		return errors;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const descriptionElement = editorRef.current;
		const updatedFormData = {
			...formData,
			description: descriptionElement
				? descriptionElement.innerHTML.trim()
				: formData.description,
		};

		// Validate before submitting
		const validationErrors = validateFormData(updatedFormData);
		if (validationErrors.length > 0) {
			setErrors(validationErrors);
			return;
		}

		try {
			setIsSubmitting(true);
			await onSave(updatedFormData);
			onClose();
		} catch (error) {
			console.error("Error updating event:", error);
			setErrors([error.message || "Failed to update event. Please try again."]);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isOpen) return null;
	return (
		<>
			<div
				className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[1000] backdrop-blur-sm"
				onClick={onClose}
			/>
			<div
				className={`fixed z-[1001] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl overflow-hidden ${
					isInteractionEnabled ? "" : "pointer-events-none"
				}`}
			>
				<form
					onSubmit={handleSubmit}
					className="flex flex-col h-full max-h-[60vh]"
				>
					{/* Header */}
					<div className="flex items-center justify-between p-4">
						<h2 className="text-xl font-semibold text-gray-900 dark:text-white">
							Edit Event
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
						>
							<X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
						</button>
					</div>

					{errors.length > 0 && (
						<div className="px-4 py-2 bg-red-50 dark:bg-red-900/50 text-red-600 dark:text-red-400 rounded-lg mb-4">
							{errors.map((error, index) => (
								<div key={index}>{error}</div>
							))}
						</div>
					)}

					{/* Form Content */}
					<div className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600">
						{/* Title */}
						<div className="space-y-1">
							<input
								type="text"
								placeholder="Add title"
								className="w-full px-0 py-2 bg-transparent border-0 border-b-2 border-gray-200 dark:border-gray-700 focus:border-blue-500 dark:focus:border-blue-500 focus:ring-0 outline-none text-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
								value={formData.title}
								onChange={(e) =>
									setFormData({ ...formData, title: e.target.value })
								}
							/>
						</div>

						{/* Time Selection */}
						<div className="space-y-4">
							<div className="flex items-center gap-4 px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg">
								<Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
								<div className="flex items-center gap-3">
									<TimePickerDropdown
										value={formData.displayStartTime}
										onChange={(time) =>
											setFormData({ ...formData, startTime: time })
										}
										isStart={true}
									/>
									<span className="text-gray-500 dark:text-gray-400">-</span>
									<TimePickerDropdown
										value={formData.displayEndTime}
										onChange={(time) =>
											setFormData({ ...formData, endTime: time })
										}
										isStart={false}
									/>
								</div>
							</div>

							<div className="relative">
								<button
									type="button"
									onClick={() => dateInputRef.current?.showPicker?.()}
									className="w-full flex items-center gap-4 px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-colors"
								>
									<Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									<span className="text-gray-900 dark:text-gray-100">
										{format(new Date(formData.date), "MM/dd/yyyy")}
									</span>
								</button>
								<input
									ref={dateInputRef}
									type="date"
									required
									className="absolute left-0 top-full opacity-0 dark:[color-scheme:dark] [color-scheme:light]"
									value={formData.date}
									onChange={(e) => {
										const selectedDate = e.target.value;
										try {
											if (selectedDate) {
												// Validate the date
												const date = new Date(selectedDate);
												if (!isNaN(date.getTime())) {
													setFormData((prev) => ({
														...prev,
														date: selectedDate,
													}));
												}
											}
										} catch (error) {
											console.error("Invalid date:", error);
											// Keep the previous valid date
										}
									}}
								/>
							</div>
						</div>

						{/* Quick Options */}
						<div className="flex items-center gap-2 px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-lg">
							<span className="text-sm text-gray-600 dark:text-gray-300">
								All-day
							</span>
							<div className="flex-1" />
							<label className="relative inline-flex items-center cursor-pointer">
								<input
									type="checkbox"
									className="sr-only peer"
									checked={formData.isAllDay}
									onChange={(e) =>
										setFormData({ ...formData, isAllDay: e.target.checked })
									}
								/>
								<div className="w-9 h-5 bg-gray-300 dark:bg-gray-600 peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-500"></div>
							</label>
						</div>

						{/* Other Options */}
						<div className="space-y-3">
							{/* Timezone Dropdown */}
							<TimezoneDropdown
								value={formData.timezone}
								onChange={(timezone) =>
									setFormData((prev) => ({ ...prev, timezone }))
								}
								isOpen={isTimezoneOpen}
								setIsOpen={setIsTimezoneOpen}
								date={formData.date}
								startTime={formData.startTime}
								endTime={formData.endTime}
							/>

							{/* Repeat Dropdown */}
							<RepeatDropdown
								value={formData.repeat}
								onChange={(repeat) =>
									setFormData((prev) => ({ ...prev, repeat }))
								}
								isOpen={isRepeatOpen}
								setIsOpen={setIsRepeatOpen}
							/>

							{/* Participants Button */}
							<button
								type="button"
								onClick={() => setIsParticipantsOpen(true)}
								className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-gray-700 dark:text-gray-200 transition-colors group"
							>
								<Users className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
								<span className="text-sm">
									{formData.participants.length > 0
										? `${formData.participants.length} participant${
												formData.participants.length > 1 ? "s" : ""
										  }`
										: "Add participants"}
								</span>
							</button>

							{/* Conferencing Button */}
							<button
								type="button"
								onClick={() => setIsConferencingOpen(true)}
								className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-gray-700 dark:text-gray-200 transition-colors group"
							>
								<Video className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
								<span className="text-sm">
									{formData.conferencing?.url
										? "Edit conferencing"
										: "Add video conferencing"}
								</span>
							</button>

							{/* Location Button */}
							<button
								type="button"
								onClick={() => setIsLocationOpen(true)}
								className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-gray-700 dark:text-gray-200 transition-colors group"
							>
								<MapPin className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
								<span className="text-sm">
									{formData.location || "Add location"}
								</span>
							</button>

							{/* Documents Button */}
							<button
								type="button"
								onClick={() => setIsDocumentsOpen(true)}
								className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-gray-700 dark:text-gray-200 transition-colors group"
							>
								<FileText className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
								<span className="text-sm">
									{formData.documents.length > 0
										? `${formData.documents.length} attachment${
												formData.documents.length > 1 ? "s" : ""
										  }`
										: "Add attachments"}
								</span>
							</button>

							{/* Description with Rich Text */}
							<div className="space-y-2">
								<div
									className={`px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-t-lg flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 ${
										isRichTextFocused
											? "border-blue-500 dark:border-blue-500"
											: ""
									}`}
								>
									<button
										type="button"
										onClick={() => handleFormat("bold")}
										className={getFormatButtonClass(activeFormats.bold)}
										title="Bold"
									>
										<Bold className="w-4 h-4" />
									</button>
									<button
										type="button"
										onClick={() => handleFormat("italic")}
										className={getFormatButtonClass(activeFormats.italic)}
										title="Italic"
									>
										<Italic className="w-4 h-4" />
									</button>
									<button
										type="button"
										onClick={() => handleFormat("underline")}
										className={getFormatButtonClass(activeFormats.underline)}
										title="Underline"
									>
										<Underline className="w-4 h-4" />
									</button>
									<div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
									<button
										type="button"
										onClick={() => handleFormat("insertUnorderedList")}
										className={getFormatButtonClass(
											activeFormats.insertUnorderedList
										)}
										title="Bullet List"
									>
										<List className="w-4 h-4" />
									</button>
									<button
										type="button"
										onClick={() => handleFormat("insertOrderedList")}
										className={getFormatButtonClass(
											activeFormats.insertOrderedList
										)}
										title="Numbered List"
									>
										<ListOrdered className="w-4 h-4" />
									</button>
								</div>
								<div
									ref={editorRef}
									contentEditable
									className="min-h-[100px] px-3 py-2 bg-gray-100/50 dark:bg-gray-800/50 rounded-b-lg 
                  text-gray-900 dark:text-gray-100 focus:outline-none 
                  [&>ul]:list-disc [&>ul]:ml-4 [&>ol]:list-decimal [&>ol]:ml-4 
                  [&>ul>li]:mb-1 [&>ol>li]:mb-1 
                  [&>ul>li]:pl-1 [&>ol>li]:pl-1
                  empty:before:content-[attr(placeholder)] 
                  empty:before:text-gray-400 
                  dark:empty:before:text-gray-500
                  [&_li]:min-h-[1.5em]"
									onFocus={() => {
										setIsRichTextFocused(true);
										setActiveFormats({
											bold: document.queryCommandState("bold"),
											italic: document.queryCommandState("italic"),
											underline: document.queryCommandState("underline"),
											insertUnorderedList: document.queryCommandState(
												"insertUnorderedList"
											),
											insertOrderedList:
												document.queryCommandState("insertOrderedList"),
										});
									}}
									onBlur={() => setIsRichTextFocused(false)}
									onKeyUp={() => {
										setActiveFormats({
											bold: document.queryCommandState("bold"),
											italic: document.queryCommandState("italic"),
											underline: document.queryCommandState("underline"),
											insertUnorderedList: document.queryCommandState(
												"insertUnorderedList"
											),
											insertOrderedList:
												document.queryCommandState("insertOrderedList"),
										});
									}}
									onMouseUp={() => {
										setActiveFormats({
											bold: document.queryCommandState("bold"),
											italic: document.queryCommandState("italic"),
											underline: document.queryCommandState("underline"),
											insertUnorderedList: document.queryCommandState(
												"insertUnorderedList"
											),
											insertOrderedList:
												document.queryCommandState("insertOrderedList"),
										});
									}}
									placeholder="Add description"
									dangerouslySetInnerHTML={{ __html: formData.description }}
								/>
							</div>

							{/* Reminders Button */}
							<button
								type="button"
								onClick={() => setIsRemindersOpen(true)}
								className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-sm text-gray-600 dark:text-gray-300"
							>
								<Bell className="w-4 h-4 text-gray-500 dark:text-gray-400" />
								<span>
									{formData.reminders.length > 0
										? `${formData.reminders.length} reminder${
												formData.reminders.length > 1 ? "s" : ""
										  }`
										: "Add reminder"}
								</span>
								<ChevronDown className="w-4 h-4 ml-auto text-gray-500 dark:text-gray-400" />
							</button>

							<div className="flex gap-2">
								{/* Status Button */}
								<button
									type="button"
									onClick={() => setIsStatusOpen(true)}
									className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-gray-700 dark:text-gray-200 transition-colors"
								>
									{formData.status === "busy" && (
										<Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									)}
									{formData.status === "free" && (
										<Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									)}
									{formData.status === "tentative" && (
										<Coffee className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									)}

									<span className="text-sm">
										{formData.status.charAt(0).toUpperCase() +
											formData.status.slice(1)}
									</span>
								</button>

								{/* Visibility Button */}
								<button
									type="button"
									onClick={() => setIsVisibilityOpen(true)}
									className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg text-gray-700 dark:text-gray-200 transition-colors"
								>
									{formData.visibility === "default" && (
										<Globe className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									)}
									{formData.visibility === "public" && (
										<Users className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									)}
									{formData.visibility === "private" && (
										<Lock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
									)}

									<span className="text-sm">
										{formData.visibility.charAt(0).toUpperCase() +
											formData.visibility.slice(1)}
									</span>
								</button>
							</div>
						</div>
					</div>

					{/* Modals */}
					{isParticipantsOpen && (
						<ParticipantsModal
							participants={formData.participants}
							onChange={(newParticipants) => {
								setFormData((prev) => ({
									...prev,
									participants: newParticipants,
								}));
								setIsParticipantsOpen(false);
							}}
							onClose={() => setIsParticipantsOpen(false)}
						/>
					)}

					{isConferencingOpen && (
						<ConferencingModal
							conferencing={formData.conferencing}
							onChange={(newConferencing) => {
								setFormData((prev) => ({
									...prev,
									conferencing: newConferencing,
								}));
								setIsConferencingOpen(false);
							}}
							onClose={() => setIsConferencingOpen(false)}
							eventTitle={formData.title}
						/>
					)}

					{isLocationOpen && (
						<LocationModal
							location={formData.location}
							onChange={(newLocation) => {
								setFormData((prev) => ({ ...prev, location: newLocation }));
								setIsLocationOpen(false);
							}}
							onClose={() => setIsLocationOpen(false)}
						/>
					)}

					{isDocumentsOpen && (
						<DocumentsModal
							documents={formData.documents}
							onChange={(newDocuments) => {
								setFormData((prev) => ({ ...prev, documents: newDocuments }));
								setIsDocumentsOpen(false);
							}}
							onClose={() => setIsDocumentsOpen(false)}
						/>
					)}

					{isRemindersOpen && (
						<RemindersModal
							reminders={formData.reminders}
							onChange={(newReminders) => {
								setFormData((prev) => ({ ...prev, reminders: newReminders }));
								setIsRemindersOpen(false);
							}}
							onClose={() => setIsRemindersOpen(false)}
						/>
					)}

					{isStatusOpen && (
						<StatusModal
							value={formData.status}
							onChange={(newStatus) => {
								setFormData((prev) => ({ ...prev, status: newStatus }));
								setIsStatusOpen(false);
							}}
							onClose={() => setIsStatusOpen(false)}
						/>
					)}

					{isVisibilityOpen && (
						<VisibilityModal
							value={formData.visibility}
							onChange={(newVisibility) => {
								setFormData((prev) => ({ ...prev, visibility: newVisibility }));
								setIsVisibilityOpen(false);
							}}
							onClose={() => setIsVisibilityOpen(false)}
						/>
					)}

					{/* Footer */}
					<div className="p-4 flex justify-end gap-2 border-t border-gray-100 dark:border-gray-800">
						<button
							type="button"
							onClick={onClose}
							disabled={isSubmitting}
							className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={isSubmitting}
							className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors relative disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px]"
						>
							{isSubmitting ? (
								<div className="flex items-center justify-center gap-2">
									<svg
										className="animate-spin h-4 w-4 text-white"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										/>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										/>
									</svg>
									<span>Saving...</span>
								</div>
							) : (
								"Save"
							)}
						</button>
					</div>
				</form>
			</div>
		</>
	);
};

export default EditEvent;
